import * as React from "react";
import TooltipOptions from "@bizform/primereact/components/tooltip/TooltipOptions";
import { CalendarViewType } from "@bizform/type/model/data_set/LookUp.model";
import { DataType } from "@bizform/type/model/model/DataType.model";
import { Static } from "@bizform/type/decorator/Static.decorator";
import {
    BaseStateModel,
    ComponentElement,
    ComponentElementDisplayMode,
    ComponentElementStatic,
    ComponentEventType
} from "@bizform/type/model/component_element/ComponentElement.model";
import {
    ComponentCategory,
    ComponentElementManifest,
    ComponentElementProperty,
    ComponentElementPropertyType
} from "@bizform/type/model/component_element/ComponentElementManifest.model";
import {
    AccessibilityDate,
    Calendar,
    CalendarProps,
    ComponentHelper,
    DateHelper,
    DateMetaData,
    Debug,
    LocaleSettings,
    ObjectHelper
} from "@bizform/helper/dist/js/bizflow/helper.min";
import { ComponentElementClass, ReactComponentElementInit } from "@bizform/core/dist/js/bizflow/core.min";
import { AbstractComponentElement } from "../../AbstractComponentElement";
import { I18n } from "../../../misc/model/I18n.model";


type Props = CalendarProps & {
};

type WrappedCompState = {
    id?: string;
    tabindex?: number;
    name?: string;
    value?: Date;
    viewDate?: Date;
    style?: object;
    className?: string;
    printStyle?: object;
    printClassName?: string;
    inline?: boolean;
    selectionMode?: string;
    inputId?: string;
    inputStyle?: object;
    inputClassName?: string;
    required?: boolean;
    readOnlyInput?: boolean;
    readOnly?: boolean;
    disabled?: boolean;
    placeholder?: string;
    showIcon?: boolean;
    icon?: string;
    showOnFocus?: boolean;
    numberOfMonths?: number;
    view?: string;
    touchUI?: boolean;
    showTime?: boolean;
    timeOnly?: boolean;
    showSeconds?: boolean;
    hourFormat?: string;
    stepHour?: number;
    stepMinute?: number;
    stepSecond?: number;
    shortYearCutoff?: string;
    hideOnDateTimeSelect?: boolean;
    locale?: LocaleSettings;
    dateFormat?: string;
    simpleDateFormat?: {input?: string, view?: string};
    panelStyle?: object;
    panelClassName?: string;
    monthNavigator?: boolean;
    yearNavigator?: boolean;
    disabledDates?: Date[];
    disabledDays?: number[];
    minDate?: Date;
    maxDate?: Date;
    maxDateCount?: number;
    showOtherMonths?: boolean;
    selectOtherMonths?: boolean;
    showButtonBar?: boolean;
    todayButtonClassName?: string;
    clearButtonStyleClass?: string;
    closeButtonStyleClass?: string;
    autoZIndex?: boolean;
    baseZIndex?: number;
    appendTo?: any;
    tooltipAttribute?: TooltipOptions & {text?: string};
    yearRange?: string;
    yearRangeFrom?: number;
    yearRangeTo?: number;

    dateTemplate? (dateMeta: DateMetaData): React.ReactNode;
    headerTemplate?(): React.ReactNode;
    footerTemplate?(): React.ReactNode;
    onFocus?(event: Event): void;
    onBlur?(event: Event): void;
    onInput?(event: Event): void;
    onSelect?(e: ComponentEventType): void;
    onChange?(e: ComponentEventType): void;
    onTodayButtonClick?(event: Event): void;
    onClearButtonClick?(event: Event): void;
    onCloseButtonClick?(event: Event): void;
    onViewDateChange?(e: ComponentEventType): void;
};

type WrappedFormState = {
    onChange?: string;
};

type State = BaseStateModel & {
    wrappedCompState: WrappedCompState;
    wrappedFormState: WrappedFormState;
};

/**
 * @since 0.0.1
 * @author Hwansoon Park
 * @class BfInlineTimePicker
 * @description BfInlineTimePicker component element
 */
@ComponentElementClass()
@Static<ComponentElementStatic<Date>>()
export class BfInlineTimePicker extends AbstractComponentElement<Props, State> {
    /* manifest */
    static readonly componentElementManifest: ComponentElementManifest = {
        uniqueId: "_LCeuXlGr5AxQVywvpc519rXvt82FMXU",
        category: ComponentCategory.CALENDAR,
        label: I18n.calendar.inlineTimePicker.label,
        description: I18n.calendar.inlineTimePicker.description,
        icon: "",
        dataType: [DataType.TIME, DataType.UTCTIME],
        property: null
    };

    focus(): void {
        if (this.accessibility) {
            this._calendar.validationFocus();
        } else if (this._calendar && this._calendar.panel) {
            this._calendar.panel.querySelector("button").focus();
        }
    }

    setProperty(property: ComponentElementProperty): void {
        try {
            const newState = ComponentHelper.createStateWithProperties<State, ComponentEventType>(
                property,
                this);
            const newProps = newState.wrappedCompState;

            newProps.view = CalendarViewType.DATE;
            newProps.inline = true;
            newProps.showIcon = false;

            newProps.showTime = true;
            newProps.timeOnly = true;
            newProps.readOnlyInput = true;

            const defaultDateFormatInput = DateHelper.getDefaultFormatByType(ComponentElementPropertyType.FORMATTIME);
            const defaultDateFormatView = DateHelper.getDefaultFormatByType(ComponentElementPropertyType.FORMATTIME);
            if (!newProps.simpleDateFormat.input || newProps.simpleDateFormat.input === "") {
                newProps.simpleDateFormat = {
                    input: !newProps.simpleDateFormat.input || newProps.simpleDateFormat.input === "" ? defaultDateFormatInput : newProps.simpleDateFormat.input,
                    view: !newProps.simpleDateFormat.view || newProps.simpleDateFormat.view === "" ? defaultDateFormatView : newProps.simpleDateFormat.view
                };
            }
            if (newProps.simpleDateFormat &&  newProps.simpleDateFormat.view) {
                newProps.showSeconds = newProps.simpleDateFormat.view.indexOf("s") > -1 ? true : false;
                newProps.hourFormat = newProps.simpleDateFormat.view.indexOf("a") > -1 ? "12" : "24";
            }
            this.setState(newState);
        } catch (e) {
            this.emitDidCatch(e);
        }
    }

    emit(value: Date): void {
    }

    emitFormState(value: string): void {
    }

    subscription(value: Date): void {
        const newState = {
            wrappedCompState: {
                ...this.state.wrappedCompState,
                value: null
            }
        };
        if (!ComponentHelper.isNull(value)) {
            value = typeof value === "string" ? null : new Date((value as Date).toString());
        }
        newState.wrappedCompState.value = value;
        this.setState(newState);
    }

    @ReactComponentElementInit(BfInlineTimePicker)
    static init(): ComponentElement<Date> {
        return;
    }

    initDone(): void {
    }

    destroy(): void {
        this.destroyDone();
    }

    destroyDone(): void {
    }

    /* for React */
    props: Props;
    state: State;

    private _calendar: any;

    constructor(props?: Props) {
        super(props);

        this.state = {
            show: false,
            displayMode: ComponentElementDisplayMode.INIT,
            wrappedCompState: {},
            wrappedFormState: {}
        };

        this._onChangeEvent = this._onChangeEvent.bind(this);
        this._setRef = this._setRef.bind(this);
    }

    componentDidUpdate(): void {
        if (this.accessibility) {
            return;
        }
        if (this._calendar && this._calendar.panel && this.state.wrappedCompState.tabindex !== this.___tabindex) {
            ComponentHelper.tabindex(this.displayMode, Array.from(this._calendar.panel.querySelectorAll("button")), this.state.wrappedCompState.tabindex);
            this.___tabindex = this.state.wrappedCompState.tabindex;
        }
    }

    componentWillUnmount(): void {
        this._calendar = null;
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
        this.emitDidCatch(error);
    }

    shouldComponentUpdate(nextProps: Readonly<Props>, nextState: Readonly<State>, nextContext: any): boolean {
        return ObjectHelper.hasDifference(nextState, this.state);
    }

    emitSubscriptionReject(): void {
    }

    emitDidCatch(error: Error | any): void {
        Debug.console.error(error);
    }

    private _onChangeEvent(e: ComponentEventType): void {
        const newProps = {
            wrappedCompState: {
                ...this.state.wrappedCompState,
                value: ObjectHelper.cloneDeep(e.value)
            }
        };
        this.setState(newProps, this._callbackSetState);
    }

    private _callbackSetState(): void {
        this.emit(this.state.wrappedCompState.value);
    }

    render(): React.ReactNode {
        const {
            show,
            displayMode
        } = this.state;

        if (!show) {
            return null;
        } else {
            if (this.accessibility && displayMode !== ComponentElementDisplayMode.PRINT) {
                return this._render4Edit();
            }
            switch (displayMode) {
                case ComponentElementDisplayMode.EDIT:
                    return this._render4Edit();
                case ComponentElementDisplayMode.READONLY:
                    return this._render4ReadOnly();
                case ComponentElementDisplayMode.DISABLED:
                    return this._render4Disabled();
                case ComponentElementDisplayMode.PRINT:
                    return this._render4Print();
                default:
                    return null;
            }
        }
    }

    private _render(newProps: WrappedCompState): React.ReactNode {
        delete(newProps["printClassName"]);
        delete(newProps["printStyle"]);
        if (this.accessibility) {
            return (
                <AccessibilityDate
                    tabindex={newProps.tabindex}
                    key={"Calendar_" + this.elementId}
                    ref={this._setRef}
                    id={this.elementId}
                    value={newProps.value}
                    displayMode={this.displayMode}
                    locale={newProps.locale}
                    simpleDateFormat={newProps.simpleDateFormat}
                    hourFormat={newProps.hourFormat}
                    onChange={newProps.onChange}
                />
            );
        } else {
            delete(newProps.tabindex);
            return (
                <Calendar
                    {...newProps}
                    key={this.elementId}
                    inputId={this.elementId}
                    style={{ "width": "100%" }}
                    ref={this._setRef}
                />
            );
        }
    }

    private _render4Edit(): React.ReactNode {
        const newProps = {
            ...this.state.wrappedCompState
        };

        return this._render(newProps);
    }

    private _render4ReadOnly(): React.ReactNode {
        const newProps = {
            ...this.state.wrappedCompState,
            readOnlyInput : true,
            showOnFocus: false,
            readOnly: true
        };

        return this._render(newProps);
    }

    private _render4Disabled(): React.ReactNode {
        const newProps = {
            ...this.state.wrappedCompState,
            disabled: true
        };

        return this._render(newProps);
    }

    private _render4Print(): React.ReactNode {
        let value: any = "";
        if (this.state.wrappedCompState.value) {
            value = DateHelper.formatter(this.state.wrappedCompState.value, this.state.wrappedCompState.simpleDateFormat.view);
        }
        return (
            <div ref={this._setRef} style={this.state.wrappedCompState ? {...this.state.wrappedCompState.printStyle} : null} className={`bf-print-mode ${this.state.wrappedCompState ? this.state.wrappedCompState.printClassName : null}`}>{value}</div>
        );
    }

    private _setRef(ref) {
        if (ref) {
            this._calendar = ref;
            this.readyComponent();
        }
    }
}
